/* eslint-disable no-underscore-dangle */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Collapse } from 'antd';
import Button from 'components/generalUI/Button';
import {
  Pack,
  EntranceTicketType,
  EntranceImage,
  ActivityImage,
  PackImage,
} from 'apollo/generated/types';
import priceFormat from 'utils/formats/priceFormat';
import isFullPack from 'utils/helpers/isFullPack';
import placeholderImage from 'assets/images/placeholder-image.png';
import BadgeIcon from 'assets/images/badge-icon.svg';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import styles from './SelectCard.module.scss';

interface Props {
  item: Pack | EntranceTicketType;
  isPack: boolean;
  images: EntranceImage[] | ActivityImage[] | PackImage[];
  purchaseTickets?: number;
  date: string;
}

const SelectCard: FC<Props> = ({
  item,
  isPack,
  images,
  purchaseTickets,
  date,
}: Props) => {
  const { t } = useTranslation();

  const { Meta, Grid } = Card;
  const firstImage = images.length ? images[0] : null;
  const isBikeTicket =
    item.name?.toLowerCase().includes('bicicleta').toString() || 'false';

  const [panelHeaderText, setPanelHeaderText] = React.useState<string>(
    t('checkout.selectEntrances.whatIncludes')
  );

  const handlePanelClick = (key: string | string[]) => {
    const isPanelOpen = key.length > 0;
    setPanelHeaderText(
      isPanelOpen
        ? t('checkout.selectEntrances.showLess')
        : t('checkout.selectEntrances.whatIncludes')
    );
  };

  const url = isPack
    ? `/tickets/packs/${item.id}?bike=${isBikeTicket}&date=${date}`
    : `/tickets/entrances/${item.id}?bike=${isBikeTicket}&date=${date}`;
  const firstParagraphDescription = (
    <div
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: item.description || '' }}
    />
  );
  const placeholder = (
    <img className={styles.CardImage} src={placeholderImage} alt="placeholder" />
  );
  const title = (
    <>
      <h3>{item.name}</h3>
      <hr className={styles.BodyHorizontalLine} />
    </>
  );

  const renderOverlay = () => {
    const isEntranceTicketTypeDisabled = !item.status && item.visibility;
    if (isEntranceTicketTypeDisabled) {
      return (
        <div className={styles.Overlay}>
          <div className={styles.ClosedTemporaryContainer}>
            <h2 className={styles.ClosedTemporaryText}>
              {t('checkout.selectEntrances.closedTemporary')}
            </h2>
          </div>
        </div>
      );
    }
    const outOfStock = !item.remaining || item.remaining <= 0;
    if (outOfStock) {
      return (
        <div className={styles.Overlay}>
          <h1 className={styles.OverlayTitle}>
            {t('checkout.selectEntrances.outOfStock')}
          </h1>
        </div>
      );
    }

    return <></>;
  };

  return (
    <div className={styles.Container}>
      {renderOverlay()}
      <Card
        className={styles.Card}
        bordered={false}
        cover={
          isPack ? (
            <Badge className={styles.CountBadge} color="#f26d31" count={purchaseTickets}>
              {isFullPack(item.id, isPack) && (
                <img src={BadgeIcon} alt="badge" className={styles.FullIcon} />
              )}
              {firstImage ? (
                <img
                  className={styles.PackCardImage}
                  alt="Pack"
                  src={firstImage?.image || ''}
                />
              ) : (
                placeholder
              )}
            </Badge>
          ) : (
            <Badge className={styles.CountBadge} color="#f26d31" count={purchaseTickets}>
              {firstImage ? (
                <img
                  className={styles.CardImage}
                  alt="Entrance"
                  src={firstImage?.image || ''}
                />
              ) : (
                placeholder
              )}
            </Badge>
          )
        }
      >
        <Meta className={styles.CardText} title={title} />

        <Grid style={{ width: '60%' }} className={styles.PriceDetail} hoverable={false}>
          <p>{t('checkout.selectEntrances.others')}</p>
          <h3 className={styles.Price}>{priceFormat.format(item.otherValue || 0)}</h3>
        </Grid>
        <Grid style={{ width: '40%' }} className={styles.PriceDetail} hoverable={false}>
          <p>{t('checkout.selectEntrances.adults')}</p>
          <h3 className={styles.Price}>{priceFormat.format(item.adultValue || 0)}</h3>
        </Grid>
        <Grid className={styles.SelectSpace} hoverable={false}>
          <Button
            href={url}
            className={styles.SelectButton}
            key={item.name}
            kind="secondary"
          >
            {t('checkout.selectEntrances.select')}
          </Button>
        </Grid>
        <Grid className={styles.DescriptionSpace} hoverable={false}>
          <Collapse ghost onChange={handlePanelClick}>
            <CollapsePanel
              className={styles.ShowMoreCollapse}
              key="1"
              header={panelHeaderText}
            >
              <p className={styles.Description}>{firstParagraphDescription}</p>
            </CollapsePanel>
          </Collapse>
        </Grid>
      </Card>
    </div>
  );
};

SelectCard.defaultProps = {
  purchaseTickets: 0,
};

export default SelectCard;
